import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { WorkIssue } from '@/types/github'

// Filter-chip state lives in the URL so views are sharable and
// the back button works. Keys are prefixed to coexist with ?filter= on /q.
export type ChipKey = 'is' | 'priority' | 'client' | 'assignee'

const CHIP_KEYS: ChipKey[] = ['is', 'priority', 'client', 'assignee']

export type ChipFilters = {
  get: (key: ChipKey) => string | null
  set: (key: ChipKey, value: string | null) => void
  apply: (issues: WorkIssue[]) => WorkIssue[]
  /** Chip keys currently set. Drives "is this a complete cohort?" decisions:
   *  a board can only manually reorder when nothing hides within-column members. */
  active: ChipKey[]
}

export function useViewFilters(): ChipFilters {
  const [searchParams, setSearchParams] = useSearchParams()

  const get = useCallback(
    (key: ChipKey) => searchParams.get(`f.${key}`),
    [searchParams],
  )

  const set = useCallback(
    (key: ChipKey, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value === null) next.delete(`f.${key}`)
          else next.set(`f.${key}`, value)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const apply = useMemo(() => {
    const is = searchParams.get('f.is')
    const priority = searchParams.get('f.priority')
    const client = searchParams.get('f.client')
    const assignee = searchParams.get('f.assignee')
    return (issues: WorkIssue[]) =>
      issues.filter((issue) => {
        if (is === 'open' && issue.state !== 'OPEN') return false
        if (is === 'closed' && issue.state !== 'CLOSED') return false
        if (priority && issue.priority !== priority) return false
        if (client && issue.client !== client) return false
        if (assignee && !issue.assignees.some((a) => a.login === assignee)) return false
        return true
      })
  }, [searchParams])

  const active = useMemo(
    () => CHIP_KEYS.filter((k) => searchParams.get(`f.${k}`)),
    [searchParams],
  )

  // Stable identity — views put this object in useMemo deps; a fresh literal
  // per render would re-filter the whole universe on every re-render.
  return useMemo(() => ({ get, set, apply, active }), [get, set, apply, active])
}
